import { Component, OnInit } from '@angular/core';
import { Background } from '../../shared/components/background/background';
import { HeroSection } from '../../shared/components/hero-section/hero-section';
import { ProcessComponent } from './components/process/process.component';
import { Button } from '../../shared/components/button/button';
import { CalendlyService } from '../../shared/services/calendly.service';
import { EdgeComponent } from "./components/edge/edge.component";
import { ConsultingProcessComponent } from "./components/consulting-process/consulting-process.component";
import { InfoCard } from '../../shared/components/info-card/info-card';
import { SeoService } from '../../shared/services/seo.service';

const PAGE_TITLE = 'Mobile App Strategy & Implementation';
const PAGE_DESCRIPTION = 'Mobile strategy and product development for B2B and B2C companies. We turn a mobile idea into a shipping iOS and Android app — strategy, design, build, and growth from one team.';
const PAGE_PATH = '/business-mobile-consulting/';
const PAGE_IMAGE = 'https://semnexus.com/images/business-mobile-consulting/backgrounds/business-mobile-consulting.avif';
const CALENDLY_URL = 'https://calendly.com/mikekordvani/30min?hide_gdpr_banner=1&background_color=0d0420&text_color=ffffff&primary_color=7c3aed';

const FAQS: { q: string; a: string }[] = [
  {
    q: 'What does a typical engagement cost?',
    a: 'A discovery engagement runs $4,000–$10,000 and produces a feature list, technical recommendation, and budget. A full build is typically $20,000–$200,000 depending on scope, integrations, and platform coverage. We share a fixed quote after discovery so there are no surprises after.',
  },
  {
    q: 'How long until we launch?',
    a: 'Discovery is 1–2 weeks. A focused first version is typically 8–16 weeks of build. Most clients are in the App Store within 4 months of kickoff. Larger platforms ship a phase-one first and add scope iteratively.',
  },
  {
    q: 'Do you only consult, or do you also build the app?',
    a: 'Both. The same team that scopes your strategy designs and ships the product. You do not hand a roadmap to a separate development shop and hope it survives translation. Strategy, design, engineering, and growth are one team here.',
  },
  {
    q: 'What tech stacks do you work with?',
    a: 'We ship native iOS and Android, and we use React Native, Flutter, and Angular/Ionic where a hybrid codebase fits the budget and feature set. Backends are typically Node, Laravel, or Python — chosen to fit your team\'s existing stack when there is one.',
  },
  {
    q: 'We already have a partial app — can you pick it up?',
    a: 'Yes. Discovery includes a technical audit of what exists. We tell you honestly whether to continue, refactor, or restart, and we scope the path forward against your budget.',
  },
];

@Component({
  selector: 'app-business-mobile-consulting',
  standalone: true,
  imports: [Background, HeroSection, ProcessComponent, Button, EdgeComponent, ConsultingProcessComponent, InfoCard],
  templateUrl: './business-mobile-consulting.component.html',
  styleUrls: ['./business-mobile-consulting.component.scss']
})
export class BusinessMobileConsultingComponent implements OnInit {

  protected file = 'business-mobile-consulting';

  constructor(
    private calendly: CalendlyService,
    private seo: SeoService,
  ) { }

  ngOnInit() {
    this.seo.apply({
      title: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      canonical: PAGE_PATH,
      image: PAGE_IMAGE,
      imageAlt: 'Mobile app strategy and implementation by SEM Nexus',
      type: 'website',
    });

    this.seo.setBreadcrumbs([
      { name: 'Home', url: '/' },
      { name: 'Services', url: '/mobile-app-marketing-services/' },
      { name: 'Mobile App Strategy & Implementation' },
    ]);

    this.seo.setJsonLd('service', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `https://semnexus.com${PAGE_PATH}#service`,
      name: 'Mobile App Strategy & Implementation',
      serviceType: 'Mobile app strategy and product development',
      description: PAGE_DESCRIPTION,
      provider: { '@id': 'https://semnexus.com/#organization' },
      areaServed: { '@type': 'Country', name: 'United States' },
      url: `https://semnexus.com${PAGE_PATH}`,
      offers: {
        '@type': 'OfferCatalog',
        name: 'Mobile app strategy and implementation engagements',
        itemListElement: [
          { '@type': 'Offer', name: 'Discovery (1–2 weeks)', description: 'Feature list, technical recommendation, realistic budget.' },
          { '@type': 'Offer', name: 'Build (8–16 weeks)', description: 'Design, development, iOS and Android shipped in parallel.' },
          { '@type': 'Offer', name: 'Launch + grow (ongoing)', description: 'App Store launch, growth experiments, version-over-version improvements.' },
        ],
      },
    });

    this.seo.setJsonLd('faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  scheduleCall(position: 'top' | 'bottom' = 'bottom') {
    // `schedule_call_click` now fires centrally inside CalendlyService.openPopup();
    // pass the position as `source` so the per-button detail is preserved.
    this.calendly.openPopup(CALENDLY_URL, `${PAGE_PATH}#${position}`).catch(() => { });
  }
}
